import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Chrome, ArrowRight, Loader2, Zap, TrendingUp, Sparkles, Search } from "lucide-react";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { SHOWCASE } from "@/lib/showcase";
import { usePageMeta, useJsonLd } from "@/lib/seo";
import { toast } from "sonner";

const CHROME_STORE_URL = "https://chromewebstore.google.com/detail/dodbbengpljjkpfapnklccapeobanpca";

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Paste any URL",
    body: "Website, app, competitor, your own project. MonetizeGPS reads publicly available signals — no login required.",
  },
  {
    n: "02",
    title: "MonetizeGPS ranks your options",
    body: "Every monetization play is scored on audience match, revenue potential, ease, and evidence — so you see only what's actually a fit.",
  },
  {
    n: "03",
    title: "Get a beautiful Blueprint",
    body: "Editorial-grade layout with concrete next steps. Share it, email it, rescan later to see what changed.",
  },
];

const FAQ = [
  {
    q: "What can I scan?",
    a: `Websites, pages, blogs, stores, creator links, YouTube channels, and videos.
Examples:
nike.com · apple.com · notion.so · canva.com · shopify.com
apple.com/iphone · notion.so/product · canva.com/pricing · shopify.com/pricing · stripe.com/payments
hubspot.com/blog · neilpatel.com/blog · shopify.com/blog · canva.com/learn
linktr.ee · beacons.ai · stan.store
youtube.com/@MrBeast · youtube.com/@mkbhd · youtube.com/watch?v=dQw4w9WgXcQ
Need https:// or www? No. Paste the link — we handle it.`,
  },
  {
    q: "What do I get for free?",
    a: "Your first lookup is a free preview — no signup required. You'll see the #1 monetization play in full. Plays #2–5, the executive summary, and API recommendations unlock when you create a free account. No card needed to sign up.",
  },
  {
    q: "How long does an analysis take?",
    a: "Most sites take 20–45 seconds. Larger sites with lots of content can take up to a minute.",
  },
  {
    q: "How does the Chrome extension work?",
    a: "It's the fastest way to use MonetizeGPS. Install it once from the Chrome Web Store, then click the MonetizeGPS icon while you're on ANY website — no copying, no pasting, no switching tabs. The extension analyzes the exact page you're browsing and gives you the score in seconds. Browsing Flippa listings? Checking out a competitor? One click and you have the Blueprint. It's free to install and uses the same lookups as the website.",
  },
  {
    q: "How much does it cost?",
    a: "1 free preview to try, then plans start at $29 one-time or $49/month for 10 Blueprints. See the Pricing page for all tiers.",
  },
  {
    q: "What counts as a lookup?",
    a: "Analyzing a NEW URL uses 1 lookup (extension or website). Diff view and sharing are always free — and on monthly plans, monitoring rescans are free too.",
  },
  {
    q: "Does MonetizeGPS keep watching a site after the first scan?",
    a: "Yes — on monthly plans, every URL you scan is automatically added to your Watchlist. Each watched site gets a full automatic rescan every month, a diff against the previous Blueprint, and an alert (in-app + email) only when something meaningful changes. No setup, no extra cost, no noise.",
  },
  {
    q: "What is Rescan?",
    a: "Rescan re-analyzes any site you've already looked up — using the latest data from that URL. Websites change constantly (new pricing pages, added checkout flows, dropped features) and Rescan catches all of it. Watched sites get 1 free manual rescan per month; beyond that, a rescan uses 1 lookup.",
  },
  {
    q: "What is Diff View?",
    a: "Diff View is where the magic happens. When you rescan a URL, MonetizeGPS compares the old and new scans side-by-side and shows you EXACTLY what changed — new monetization opportunities that appeared, plays that dropped in score, signal changes on the site itself, and specific next actions. It's like a stock ticker for monetization strategy. Diff View is included free once you rescan.",
  },
  {
    q: "Is this legal?",
    a: "Yes. MonetizeGPS only looks at publicly available information — the same content any visitor sees in their browser. It respects robots.txt and refuses to analyze sites that prohibit it (Twitter/X, LinkedIn, Reddit, login-walled pages).",
  },
  {
    q: "How does the scoring work?",
    a: "Each play is scored on four axes — Audience match, Revenue potential, Implementation ease, and Evidence strength. Only plays scoring ≥ 65 are shown.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Landing() {
  usePageMeta({
    title: "MonetizeGPS — Monetization directions for any website",
    description:
      "Paste any URL and get the top ways to monetize it — scored, ranked, and brutally honest. 1 free preview, no signup needed.",
    path: "/",
  });
  useJsonLd("faq-jsonld", FAQ_JSONLD);
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [tiers, setTiers] = useState([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    apiClient.get("/pricing").then((r) => setTiers(r.data?.packages || [])).catch(() => {});
  }, []);

  const normalize = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const target = normalize(url);
    if (!target || target.length < 8 || !/\./.test(target)) {
      toast.error("Enter a real URL (e.g. techcrunch.com)");
      return;
    }
    setAnalyzing(true);
    try {
      // Start the scan as a background job (mobile-safe: no long-held request),
      // then hand off to the /analyzing terminal page which polls for the result.
      const { data } = await apiClient.post("/analyze/start", { url: target });
      navigate(`/analyzing?job=${data.job_id}&url=${encodeURIComponent(target)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const message = typeof detail === "object" ? detail?.message : detail;
      if (code === "signup_required") {
        toast.info(message || "Sign up to keep going.");
        setTimeout(() => navigate("/pricing"), 900);
      } else if (code === "out_of_lookups") {
        toast.info(message || "Out of lookups.");
        setTimeout(() => navigate("/pricing"), 900);
      } else {
        toast.error(message || "Analysis failed. Try another URL.");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen grain" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-20 pb-24">
          <div className="flex items-center gap-3 mb-10">
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
              § Monetization intelligence
            </span>
            <span className="flex-1 h-px bg-[#111]" />
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
              1 free preview · no signup needed
            </span>
          </div>

          <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.85] tracking-tighter text-[#111] max-w-5xl">
            Spot the untapped revenue.<br />
            That&apos;s the <em className="text-[#1E40AF] not-italic">Blueprint</em>.
          </h1>

          <p className="mt-8 text-lg md:text-xl text-[#333] max-w-2xl leading-relaxed">
            Paste any URL. Get the top ways to monetize it — scored, ranked, and brutally honest. Every play backed by real evidence from the site itself.
          </p>

          {/* URL input — the primary CTA */}
          <form onSubmit={submit} className="mt-10 max-w-2xl" data-testid="landing-scan-form">
            <div className="flex flex-col sm:flex-row gap-3 border-2 border-[#111] bg-white shadow-brutal-lg">
              <div className="flex items-center gap-3 flex-1 px-5 py-4">
                <Search size={18} className="text-[#555] shrink-0" />
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="www.company.com"
                  disabled={analyzing}
                  className="flex-1 font-sans-plex text-base md:text-lg bg-transparent outline-none placeholder:text-[#999] w-full min-w-0"
                  data-testid="landing-url-input"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="url"
                />
              </div>
              <button
                type="submit"
                disabled={analyzing || !url.trim()}
                className="bg-[#111] text-[#F4F4F0] px-8 py-4 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shrink-0"
                data-testid="landing-scan-button"
              >
                {analyzing ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Analyzing...
                  </>
                ) : (
                  <>
                    Analyze URL <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
            <p className="mt-3 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555]">
              ⚡ Free preview · Ready in under a minute · Sign up to unlock the full Blueprint
            </p>
            <p className="mt-1 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#777]">
              🔒 By continuing, you confirm you have the right to analyze this URL
            </p>
          </form>

          {/* Extension option — bolder banner (desktop only — extensions don't work on mobile Chrome) */}
          <div className="mt-14 border-2 border-[#111] bg-[#111] text-[#F4F4F0] p-5 md:p-6 hidden md:flex md:items-center gap-5 shadow-brutal-lg" data-testid="chrome-banner">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 shrink-0 border-2 border-[#F4F4F0] flex items-center justify-center">
                <Chrome size={22} className="text-[#F4F4F0]" />
              </div>
              <div>
                <div className="font-serif-display text-xl md:text-2xl font-bold leading-tight text-[#F4F4F0]">
                  Analyze while you browse
                </div>
                <div className="mt-1 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#F4F4F0]/70">
                  Install once · One click analyzes the site you&apos;re on · No copy-paste needed
                </div>
              </div>
            </div>
            <a
              href={CHROME_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#FF331F] text-white px-6 py-4 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-[#111] transition-colors shrink-0"
              data-testid="install-extension-cta"
            >
              <Chrome size={14} /> Add to Chrome <ArrowRight size={14} />
            </a>
          </div>
          <div className="mt-3 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#0A8754] hidden md:block">
            ⚡ 30-second setup · Works on any public site
          </div>
        </div>
      </section>

      {/* SHOWCASE STRIP — real unlocked sample Blueprints as instant proof */}
      <section className="border-b-2 border-[#111] bg-white" data-testid="landing-showcase-strip">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
            <div>
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-2">
                § Proof, not promises
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-black tracking-tighter leading-[0.9]">
                Read a real Blueprint before you run one.
              </h2>
            </div>
            <Link
              to="/showcase"
              className="font-mono-plex text-xs uppercase tracking-[0.25em] text-[#FF331F] hover:underline shrink-0"
              data-testid="landing-showcase-all-link"
            >
              All 5 samples →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border-2 border-[#111]">
            {SHOWCASE.map((s, i) => (
              <Link
                key={s.id}
                to={`/report/${s.id}`}
                className={`group bg-white p-5 hover:bg-[#F4F4F0] transition-colors ${i < SHOWCASE.length - 1 ? "border-b-2 sm:border-b-0 lg:border-r-2" : ""} ${i % 2 === 0 && i < SHOWCASE.length - 1 ? "sm:border-r-2 lg:border-r-2" : ""} border-[#111]`}
                data-testid={`landing-showcase-card-${i}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-serif-display text-xl font-bold leading-tight">
                    {s.flag} {s.name}
                  </div>
                  <span className="score-display text-3xl leading-none" style={{ color: s.score >= 85 ? "#0A8754" : s.score >= 70 ? "#111111" : "#F5A623" }}>
                    {s.score}
                  </span>
                </div>
                <div className="mt-1 font-mono-plex text-[10px] uppercase tracking-[0.2em] text-[#555]">
                  {s.domain}
                </div>
                <div className="mt-3 inline-block font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] bg-[#F4F4F0] group-hover:bg-white px-2 py-0.5">
                  Top play: {s.topPlay}
                </div>
                <div className="mt-4 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#FF331F] opacity-0 group-hover:opacity-100 transition-opacity">
                  Read it →
                </div>
              </Link>
            ))}
          </div>
          <p className="mt-3 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555]">
            ⚡ Fully unlocked · No signup needed · Honest scores — a 72 stays a 72
          </p>
        </div>
      </section>

      {/* HOW IT WORKS — 3 steps, minimalist */}
      <section id="how-it-works" className="border-b-2 border-[#111] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-2xl mb-14">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
              § How it works
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-black tracking-tighter leading-[0.9]">
              Three steps.<br />No fluff.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[#111]" data-testid="how-it-works-grid">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={step.n}
                className={`p-8 md:p-10 ${i < HOW_IT_WORKS.length - 1 ? "border-b-2 md:border-b-0 md:border-r-2 border-[#111]" : ""}`}
                data-testid={`how-step-${i}`}
              >
                <div className="score-display text-5xl md:text-6xl text-[#FF331F] leading-none">{step.n}</div>
                <h3 className="mt-5 font-serif-display text-2xl font-bold leading-tight">{step.title}</h3>
                <p className="mt-3 text-sm text-[#333] leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT FOR BUYERS — marketplace users */}
      <section id="built-for-buyers" className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-6">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
                § Built for buyers
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-black tracking-tighter leading-[0.9]">
                Evaluating what&apos;s for sale?
              </h2>
            </div>
            <p className="md:col-span-6 text-base text-[#333] leading-relaxed md:mt-8">
              Whether you&apos;re screening a Flippa listing, deep-diving an Empire Flippers deal, or benchmarking a competitor — MonetizeGPS cuts your due-diligence time from hours to seconds. Paste the URL, get the Blueprint.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border-2 border-[#111] bg-white" data-testid="marketplace-grid">
            {[
              { name: "Flippa", tag: "Websites · Apps · Domains", use: "Screen hundreds of listings/week" },
              { name: "Empire Flippers", tag: "$10K–$10M websites", use: "Deep diligence on premium deals" },
              { name: "Motion Invest", tag: "Smaller content sites", use: "Quick-hit acquisition screening" },
              { name: "Fe International", tag: "Enterprise SaaS", use: "High-value M&A" },
              { name: "Product Hunt", tag: "New launches", use: "Spot rising indie hackers" },
            ].map((m, i, arr) => (
              <div
                key={m.name}
                className={`p-5 md:p-6 border-[#111] ${i < arr.length - 1 ? "border-b-2 lg:border-b-0 lg:border-r-2" : ""}`}
                data-testid={`marketplace-${i}`}
              >
                <div className="font-mono-plex text-[9px] uppercase tracking-[0.25em] text-[#FF331F]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-2 font-serif-display text-xl font-black leading-none">{m.name}</h3>
                <div className="mt-2 font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">{m.tag}</div>
                <p className="mt-3 text-sm text-[#333]">{m.use}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 font-mono-plex text-[10px] uppercase tracking-widest text-[#777] text-center">
            * Works best on live sites with real content and traffic signals. Empty pre-launch pages may return limited results.
          </p>
        </div>
      </section>

      {/* RESCAN + DIFF — power feature explanation */}
      <section id="rescan-diff" className="border-b-2 border-[#111] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-3xl mb-12">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
              § Rescan + Diff · Where the compound value lives
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-black tracking-tighter leading-[0.9]">
              Track how a site&apos;s monetization<br/>changes over time.
            </h2>
            <p className="mt-6 text-lg text-[#333] leading-relaxed">
              Any URL you analyze can be rescanned later. MonetizeGPS then compares the two Blueprints and shows you exactly what changed — new opportunities that appeared, plays that dropped in score, signal shifts on the site itself, and the specific next action to take.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-2 border-[#111]">
            <div className="p-6 md:p-8 bg-white border-b-2 md:border-b-0 md:border-r-2 border-[#111]" data-testid="feature-rescan">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] mb-2">
                ▸ Rescan
              </div>
              <h3 className="font-serif-display text-3xl font-black leading-none">Same URL. Fresh data.</h3>
              <p className="mt-4 text-base text-[#333] leading-relaxed">
                Websites change constantly — new pricing pages, added checkout flows, launched newsletters, dropped features. Rescan re-analyzes the same URL from scratch, using the latest data. Perfect for monitoring a competitor, tracking a client site, or watching your own launches evolve.
              </p>
              <div className="mt-5 font-mono-plex text-xs uppercase tracking-widest text-[#555]">
                1 Blueprint per rescan · Included in every monthly plan
              </div>
            </div>

            <div className="p-6 md:p-8 bg-[#111] text-[#F4F4F0]" data-testid="feature-diff">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] mb-2">
                ▸ Diff View
              </div>
              <h3 className="font-serif-display text-3xl font-black leading-none text-[#F4F4F0]">Only the changes.</h3>
              <p className="mt-4 text-base leading-relaxed text-[#F4F4F0]/90">
                MonetizeGPS compares the old Blueprint to the new one and produces a structured diff: score changes with direction (↑↓), plays that appeared, plays that dropped, signal shifts on the site, and a single &quot;what to do next&quot; recommendation. Think of it like a stock ticker for monetization strategy.
              </p>
              <div className="mt-5 font-mono-plex text-xs uppercase tracking-widest text-[#F4F4F0]/70">
                Free with every rescan · No extra cost
              </div>
            </div>
          </div>

          <div className="mt-8 border-2 border-[#111] bg-[#F4F4F0] p-5 flex items-start gap-4" data-testid="rescan-power-note">
            <div className="score-display text-5xl leading-none text-[#FF331F] shrink-0">→</div>
            <div>
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-1">
                Why this is the killer feature
              </div>
              <p className="text-base text-[#222] leading-relaxed">
                Anyone can analyze a site once. But being able to <em>rescan a URL 6 months later and see exactly what changed</em> is what turns MonetizeGPS from a one-time tool into a compounding asset. Track 20 sites monthly and you have real intelligence competitors don&apos;t.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
                § Pricing
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-black tracking-tighter leading-[0.9]">
                Fair, honest, no BS.
              </h2>
            </div>
            <p className="md:col-span-5 text-base text-[#333] leading-relaxed">
              1 free preview to try. After that, pick a plan that fits — or grab a single Blueprint for $29.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border-2 border-[#111] bg-white" data-testid="pricing-teaser">
            {tiers.map((t, i) => (
              <MiniTier
                key={t.id}
                tier={t}
                highlighted={t.highlighted === true}
                last={i === tiers.length - 1}
                index={i}
                total={tiers.length}
                onClick={() => navigate("/pricing")}
              />
            ))}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center gap-2 border-2 border-[#111] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
              data-testid="see-all-pricing"
            >
              See full details <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-[#111] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">§ FAQ</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-black tracking-tight leading-none">
              Common<br />questions.
            </h2>
          </div>
          <div className="md:col-span-8 border-2 border-[#111] divide-y-2 divide-[#111]">
            {FAQ.map((f, i) => (
              <details key={i} className="group p-5" data-testid={`landing-faq-${i}`}>
                <summary className="cursor-pointer font-serif-display text-lg font-bold list-none flex justify-between items-start gap-4">
                  <span>{f.q}</span>
                  <span className="font-mono-plex text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-[#333] leading-relaxed whitespace-pre-line">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#111] bg-[#F4F4F0]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-serif-display text-xl font-bold">MonetizeGPS</div>
          <div className="flex items-center gap-6 font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555]">
            <button onClick={() => navigate("/pricing")} className="hover:text-[#FF331F]">Pricing</button>
            <button onClick={() => navigate("/install")} className="hover:text-[#FF331F] hidden md:inline-block">Install</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#FF331F]">Privacy</button>
            <button onClick={() => navigate("/terms")} className="hover:text-[#FF331F]">Terms</button>
            <a href="mailto:support@monetizegps.com" className="hover:text-[#FF331F]">Support</a>
          </div>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555]">
            © {new Date().getFullYear()} MonetizeGPS
          </div>
        </div>
      </footer>
    </div>
  );
}

function MiniTier({ tier, highlighted, last, index, total, onClick }) {
  const isMonthly = tier.kind === "monthly_pass";
  const rightBorder = index < total - 1 ? "lg:border-r-2" : "";
  // On 2-col mobile grid, last odd item takes full column — add bottom border logic
  const bottomBorder = index < total - (total % 2 === 0 ? 2 : 1) ? "border-b-2 lg:border-b-0" : "";
  return (
    <button
      onClick={onClick}
      className={`text-left p-5 md:p-6 border-[#111] ${rightBorder} ${bottomBorder} ${highlighted ? "bg-[#111] text-[#F4F4F0]" : "bg-white text-[#111] hover:bg-[#F4F4F0]"} transition-colors`}
      data-testid={`teaser-${tier.id}`}
    >
      <div className={`font-mono-plex text-[9px] uppercase tracking-[0.25em] ${highlighted ? "text-[#F4F4F0]/70" : "text-[#555]"}`}>
        {highlighted ? "Most popular" : String(index + 1).padStart(2, "0")}
      </div>
      <h3 className={`mt-2 font-serif-display text-xl md:text-2xl font-black leading-none ${highlighted ? "text-[#F4F4F0]" : ""}`}>
        {tier.name}
      </h3>
      <div className="mt-3 flex items-baseline gap-1">
        <span className={`score-display text-3xl md:text-4xl leading-none ${highlighted ? "text-[#F4F4F0]" : "text-[#111]"}`}>
          ${Math.floor(tier.price)}
        </span>
        <span className={`font-mono-plex text-[10px] ${highlighted ? "text-[#F4F4F0]/70" : "text-[#555]"}`}>
          {isMonthly ? "/mo" : "once"}
        </span>
      </div>
      <div className={`mt-2 font-mono-plex text-[10px] uppercase tracking-widest ${highlighted ? "text-[#F4F4F0]/80" : "text-[#333]"}`}>
        {tier.credits} lookup{tier.credits === 1 ? "" : "s"}
      </div>
    </button>
  );
}
